import React from "react";

import HorizontalBarChart from "@charty/horizontal-bar-chart";
import { useAuthorWidgetData } from "@radarly/core/widgets/use-cases";
import { useHorizontalBarChartAuthorsWidget } from "@radarly/core/widgets/use-cases";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import { SearchableInfluencer } from "@radarly/models/influencer";
import { SearchFragmentUsers } from "@radarly/models/search";
import Empty from "../../empty";
import Loading from "../../loading";
import Tooltip from "../../tooltip/metric";

interface SharedWidgetsAuthorsHorinzontalBarChartProps {
  project: number;
  fragment: SearchFragmentUsers;
  dataviz: DatavizHorizontalBarChart;
  className?: string;
  onClickInfluencer?: (influencer: SearchableInfluencer) => void;
  forceReload?: boolean;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
  closeFullscreen?: () => void;
}

type InnerProps = Omit<
  SharedWidgetsAuthorsHorinzontalBarChartProps,
  "project" | "forceReload"
> & {
  data: SearchFragmentUsers["data"];
};

function Inner({
  data,
  fragment,
  dataviz,
  className,
  onClickInfluencer,
  disableAnimation,
  isFullscreen,
  closeFullscreen,
}: InnerProps) {
  const {
    metric,
    range,
    locale,
    gauges,
    limit,
    sort,
    mainUnit,
    secondaryValue,
    order,
    color,
    onClickGauge,
  } = useHorizontalBarChartAuthorsWidget(
    data,
    fragment,
    dataviz,
    onClickInfluencer
  );
  const _onClickGauge = (uid: string | number) => {
    if (isFullscreen) {
      closeFullscreen();
    }
    onClickGauge(uid);
  };
  return (
    <Empty empty={!gauges.length}>
      <HorizontalBarChart
        gauges={gauges}
        className={className}
        limit={limit}
        locale={locale}
        showAvatar={true}
        customColor={color}
        onClickGauge={onClickGauge ? _onClickGauge : null}
        mainUnit={mainUnit}
        secondaryValue={secondaryValue}
        sort={sort}
        order={order}
        isFullscreen={isFullscreen}
        tooltip={(serie) => (
          <Tooltip
            range={range}
            count={serie.count}
            color={color || serie.color}
            label={serie.label}
            metric={metric}
            locale={locale}
          />
        )}
        disableAnimation={disableAnimation}
      />
    </Empty>
  );
}

export default function SharedWidgetsAuthorsHorinzontalBarChart({
  project,
  fragment,
  forceReload,
  ...props
}: SharedWidgetsAuthorsHorinzontalBarChartProps) {
  const { loading, data, isEmpty } = useAuthorWidgetData(project, fragment);
  return (
    <Loading loading={loading} forceReload={forceReload}>
      <Empty empty={isEmpty}>
        <Inner data={data} fragment={fragment} {...props} />
      </Empty>
    </Loading>
  );
}
